import { useCallback, useEffect, useRef, useState } from 'react';
import { logBackgroundError } from '../observability';
import { session } from '../lib/safeStorage';

/**
 * The backup timer of the jury (#600). The Sarama music is the main part of
 * the card. The timer serves when the timekeeper leaves the gong for a short
 * time. It is not the official time (operator, 2026-10-14). It counts no
 * rounds and no rest: the jury starts each round by hand.
 *
 * Ready → Running → Paused (TIME) → Running → Done at 0:00, and Start again
 * for the next round. Reset gives Ready from each state. The timer computes
 * the time from timestamps, not from a count of ticks. Thus the time stays
 * correct when the browser slows the timers of a hidden page. The state is
 * in `sessionStorage` for the bout, so a reload restores the timer. An end
 * that passed more than 2 s ago (the page was hidden or asleep) changes the
 * state with no sound.
 */

export type ClockPhase = 'ready' | 'running' | 'paused' | 'done';

interface ClockState {
  phase: ClockPhase;
  /** While the timer runs: when it ends (Date.now() in ms). */
  endsAt: number | null;
  /** While paused (TIME): the time left, in ms. */
  leftMs: number | null;
}

export interface ClockEvents {
  onPause?: () => void;
  onResume?: () => void;
  /** `late`: the time ended while the page was hidden; no sound. */
  onEnd?: (late: boolean) => void;
}

const LATE_MS = 2000;

/** A device in power saving refuses each request of the wake lock: Start,
 *  Stop, Continue and each return to the page. The telemetry gets the first
 *  refusal only, until a request succeeds again. */
let wakeLockRefusalLogged = false;
function logWakeLockRefusal(err: unknown) {
  if (wakeLockRefusalLogged) return;
  wakeLockRefusalLogged = true;
  logBackgroundError('RoundClock:wake-lock')(err);
}
const READY: ClockState = { phase: 'ready', endsAt: null, leftMs: null };
const PHASES: readonly ClockPhase[] = ['ready', 'running', 'paused', 'done'];
// A new key: the round clock before it stored another shape (a round, a rest).
const storageKey = (boutId: string) => `lmf:bout-timer:${boutId}`;

/** The stored state, or `null`. A blocked storage, a damaged value and a shape with an unknown phase give `null`. */
function load(boutId: string): ClockState | null {
  const state = session.getJson<ClockState | null>(storageKey(boutId), null);
  return state && PHASES.includes(state.phase) ? state : null;
}

/** With the storage off, the timer of this page view still works. */
function save(boutId: string, state: ClockState): void {
  session.setJson(storageKey(boutId), state);
}

export function useRoundClock(boutId: string, roundSec: number, events: ClockEvents) {
  const [state, setState] = useState<ClockState>(() => load(boutId) ?? READY);
  const [now, setNow] = useState(() => Date.now());
  const eventsRef = useRef(events);
  eventsRef.current = events;

  useEffect(() => { save(boutId, state); }, [boutId, state]);

  // The tick, only while the time runs.
  useEffect(() => {
    if (state.phase !== 'running') return;
    const id = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(id);
  }, [state.phase]);

  // The end of the time.
  useEffect(() => {
    if (state.phase !== 'running' || state.endsAt === null || now < state.endsAt) return;
    eventsRef.current.onEnd?.(now - state.endsAt > LATE_MS);
    setState({ phase: 'done', endsAt: null, leftMs: null });
  }, [now, state]);

  // The screen stays awake while the time runs or waits at TIME. A hidden
  // page loses the lock, so it is taken again when the page shows.
  useEffect(() => {
    const active = state.phase === 'running' || state.phase === 'paused';
    const wakeLock = (navigator as Navigator & { wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> } }).wakeLock;
    if (!active || !wakeLock) return;
    let alive = true;
    let sentinel: { release: () => Promise<void> } | null = null;
    const request = () => {
      wakeLock.request('screen')
        .then(s => { wakeLockRefusalLogged = false; if (alive) sentinel = s; else void s.release(); })
        .catch(logWakeLockRefusal);
    };
    request();
    const onVisible = () => { if (document.visibilityState === 'visible') request(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', onVisible);
      sentinel?.release().catch(logBackgroundError('RoundClock:wake-release'));
    };
  }, [state.phase]);

  /** The full time from Ready, or again after 0:00 for the next round. */
  const start = useCallback(() => {
    if (state.phase !== 'ready' && state.phase !== 'done') return;
    const t = Date.now();
    setState({ phase: 'running', endsAt: t + roundSec * 1000, leftMs: null });
    setNow(t);
  }, [state.phase, roundSec]);

  const pause = useCallback(() => {
    if (state.phase !== 'running' || state.endsAt === null) return;
    setState({ phase: 'paused', endsAt: null, leftMs: Math.max(0, state.endsAt - Date.now()) });
    eventsRef.current.onPause?.();
  }, [state]);

  const resume = useCallback(() => {
    if (state.phase !== 'paused') return;
    const t = Date.now();
    setState({ phase: 'running', endsAt: t + (state.leftMs ?? 0), leftMs: null });
    setNow(t);
    eventsRef.current.onResume?.();
  }, [state]);

  /** The full time again, from each state. The music does not change. */
  const reset = useCallback(() => { setState(READY); }, []);

  const leftAt = useCallback((at: number) => (
    state.phase === 'running' ? Math.max(0, (state.endsAt ?? at) - at)
      : state.phase === 'paused' ? (state.leftMs ?? 0)
      : state.phase === 'ready' ? roundSec * 1000
      : 0
  ), [state, roundSec]);
  const remainingMs = leftAt(now);
  /** The time left at this moment, not at the last tick: for the ring display (#601). */
  const leftNow = useCallback(() => leftAt(Date.now()), [leftAt]);

  return { phase: state.phase, remainingMs, leftNow, start, pause, resume, reset };
}

/** `m:ss` of a time in ms, rounded up: a round shows 1:00 until a full second passes. */
export function formatClock(ms: number): string {
  const total = Math.ceil(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
